import React, { useMemo, useState } from "react";
import { ADVERTISER, OVERVIEW } from "../../../../../core/theme/reportTheme";
import { LISTING_COLUMNS, listingDefaultCompare } from "../../../../../core/data/advertiserSorts";
import { sortRows, nextSort } from "../../../../../core/data/sortRows";
import { CHANNEL_NAMES } from "../../../../../core/data/marketOverviewText";
import SortButton from "../../../components/SortButton";

// "Link a map pack listing": this job's map pack listings with no website of
// their own. Link one to a website, split a likely match back out, or undo.
// Saves straight away; both report pages recalculate at once.
// listings: from listingsForLinking(); websites: the websites to offer
// (the pinned one first); onSetListingLink(listingKey, action, domain).
export default function LinkListingsPanel({ listings, websites, onSetListingLink, onClose }) {
  const [sort, setSort] = useState(null);
  const [busy, setBusy] = useState(null);
  const rows = useMemo(() => sortRows(listings, sort, LISTING_COLUMNS, listingDefaultCompare), [listings, sort]);
  const sortProps = { sort, onSort: column => setSort(nextSort(sort, column, LISTING_COLUMNS)) };

  const save = async (listingKey, action, domain) => {
    setBusy(listingKey);
    const error = await onSetListingLink(listingKey, action, domain);
    setBusy(null);
    if (error) alert(`Couldn't save the link: ${error}`);
  };

  return (
    <div className={`${OVERVIEW.panel} flex-1 border-0`}>
      <div className={OVERVIEW.panelHeader}>
        <div className={OVERVIEW.panelTitleRow}>
          <h3 className={OVERVIEW.panelTitle}>Link a {CHANNEL_NAMES.local} listing</h3>
          <span className={OVERVIEW.panelNote}>listings with no website of their own · a link applies in every snapshot</span>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {sort && <button type="button" onClick={() => setSort(null)} className={OVERVIEW.link}>Back to default order</button>}
          <button type="button" onClick={onClose} className="w-6 h-6 rounded-md text-sm text-slate-500 hover:text-slate-900 hover:bg-violet-100" aria-label={`Close Link a ${CHANNEL_NAMES.local} listing`}>✕</button>
        </div>
      </div>
      <div className={OVERVIEW.panelScroll}>
        {rows.length === 0 ? (
          <p className="px-4 py-6 text-sm text-slate-400 text-center">Every {CHANNEL_NAMES.local} listing in this snapshot has its own website.</p>
        ) : (
          <table className="w-full border-separate border-spacing-0">
            <thead>
              <tr>
                <th className={OVERVIEW.thFirst}><SortButton column="name" label="Name" {...sortProps} /></th>
                <th className={OVERVIEW.thCenter}><SortButton column="postcodes" label="Postcodes" {...sortProps} /></th>
                <th className={OVERVIEW.th}><SortButton column="linkedTo" label="Linked to" {...sortProps} /></th>
                <th className={OVERVIEW.th} />
              </tr>
            </thead>
            <tbody>
              {rows.map(l => (
                <ListingRow key={l.listingKey} listing={l} websites={websites} busy={busy === l.listingKey} onSave={save} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function ListingRow({ listing, websites, busy, onSave }) {
  const [choice, setChoice] = useState(websites[0] || "");
  const l = listing;
  const kept = l.link && l.link.action === "unlink";

  let linked;
  if (l.linkedTo) {
    linked = (
      <span className="flex items-center gap-1.5 min-w-0">
        <span className="truncate">{l.linkedTo}</span>
        <span className={OVERVIEW.tag}>{l.basis === "manual" ? "linked" : "likely match"}</span>
      </span>
    );
  } else {
    linked = <span className={OVERVIEW.muted}>{kept ? "kept separate" : "–"}</span>;
  }

  return (
    <tr className="hover:bg-violet-50">
      <td className={`${OVERVIEW.tdFirst} max-w-[12.5rem] truncate text-slate-900`} title={l.name}>{l.name}</td>
      <td className={OVERVIEW.tdCenter}>{l.postcodes}</td>
      <td className={`${OVERVIEW.td} max-w-[11.25rem]`}>{linked}</td>
      <td className={`${OVERVIEW.td} text-right pr-4`}>
        <div className="flex items-center justify-end gap-2">
          {busy && <span className={OVERVIEW.muted}>Saving…</span>}
          {!busy && (l.basis === "manual" || kept) && (
            <button type="button" className={ADVERTISER.action} onClick={() => onSave(l.listingKey, null)}>{kept ? "Undo" : "Remove link"}</button>
          )}
          {!busy && l.basis === "likely" && (
            <button type="button" className={ADVERTISER.action} onClick={() => onSave(l.listingKey, "unlink")}>Unlink</button>
          )}
          {!busy && l.basis !== "manual" && websites.length > 0 && (
            <>
              <select className={ADVERTISER.smallSelect} value={choice} onChange={e => setChoice(e.target.value)} aria-label={`Website to link ${l.name} to`}>
                {websites.map(w => <option key={w} value={w}>{w}</option>)}
              </select>
              <button type="button" className={ADVERTISER.pinButton} disabled={!choice} onClick={() => onSave(l.listingKey, "link", choice)}>Link</button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}
